import React, { useState, useEffect } from 'react';
import { gql, useLazyQuery, useApolloClient } from '@apollo/client';
import { Container, Grid, Box, TextField, Button, MenuItem, Typography } from '@mui/material';
import { Icon } from '@iconify/react';
import DataTable from 'react-data-table-component';
import CustomLoader from '../components/Loader/CustomLoader';
import { customStyles } from '../utils/tableStyleForSore';
import useGlobalStyles from '../utils/globalStyles';
import { GetOrdersByTimeSlot, getInvoiceByOrderId, GetLocalStoreCustomerDeliverySlot, getOrderDetailsforworker } from '../apollo'; // Assuming this path is correct
import jsPDF from 'jspdf';
import 'jspdf-autotable'; // Import for autoTable functionality

// --- Helper Functions ---
const getSellingPrice = (prices, customerType) => {
    if (!prices || prices.length === 0) return 0;
    // Prioritize the specific customerType, if not found, use the first available price
    const priceEntry = prices.find(p => p.customerType === customerType);
    return priceEntry ? priceEntry.sellingPrice : (prices[0] && prices[0].sellingPrice) || 0;
};

const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const options = { year: 'numeric', month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' };
    return new Date(dateString).toLocaleDateString(undefined, options);
};

// UPDATED: getPaymentMethod as per your request
const getPaymentMethod = (statusNumber) => {
    const statusMap = {
        0: "COD",
        1: "ONLINE",
    };
    return statusMap[statusNumber] || "N/A"; // Return "N/A" for undefined keys
};

// UPDATED: getPaymentStatus as per your request
const getPaymentStatus = (statusNumber) => {
    const statusMap = {
        0: "PENDING",
        1: "COMPLETED",
    };
    return statusMap[statusNumber] || "N/A"; // Return "N/A" for undefined keys
};

// Customer Type mapping (retained from previous update)
const customerTypeMapping = {
    18: 'Premium',
    19: 'Regular'
    // Add other customerType mappings here if they exist in your data
};

const getCustomerTypeLabel = (customerTypeCode) => {
    return customerTypeMapping[customerTypeCode] || 'N/A';
};

// --- End Helper Functions ---


// --- GraphQL Query Definitions ---
const GET_LOCAL_STORE_CUSTOMER_DELIVERY_SLOT = gql`
    ${GetLocalStoreCustomerDeliverySlot}
`;

const GET_ORDERS_BY_TIME_SLOT = gql`
    ${GetOrdersByTimeSlot}
`;

const GET_ORDER_DETAILS_BY_ID = gql`
    ${getOrderDetailsforworker}
`;
// --- End GraphQL Query Definitions ---


const statuses = {
    '-1': 'All',
    1: 'Confirmed',
    2: 'Packing',
    3: 'Order billing',
    4: 'Ready for delivery',
    5: 'Rider Assigned',
    6: 'Out for delivery',
    7: 'Delivered',
    8: 'Cancelled',
};

const OrdersbyTimeSlot = () => {
    const [timeSlots, setTimeSlots] = useState([]);
    const [selectedTimeSlot, setSelectedTimeSlot] = useState('');
    const [orderDate, setOrderDate] = useState('');
    const [orderStatus, setOrderStatus] = useState(-1);
    const [tableData, setTableData] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const globalClasses = useGlobalStyles();
    const client = useApolloClient();

    const [pagination, setPagination] = useState({ skip: 0, limit: 10 });

    // Helper function to render billing details
    const renderBillingDetail = (docInstance, label, value, currentY, rightAlignX, labelX, strikeThrough = false) => {
        let formattedValue;

        if (label.includes('Discount') || label.includes('Coupon') || label.includes('Wallet') || label.includes('Nest') || label.includes('Savings') || label.includes('ReferralInfo')) {
            formattedValue = value >= 0 ? `-${value}` : `${value}`;
        } else if (label.includes('Delivery') || label.includes('Handling')) {
            formattedValue = value >= 0 ? `+${value}` : `${value}`;
        } else {
            formattedValue = value >= 0 ? ` ${value}` : `${value}`;
        }

        docInstance.text(label, labelX, currentY);

        if (strikeThrough) {
            docInstance.setTextColor(150);
            docInstance.line(rightAlignX - 10, currentY - 2, rightAlignX + 10, currentY - 2);
        }

        docInstance.text(formattedValue, rightAlignX, currentY);
        docInstance.setTextColor(0); // Reset text color
        return currentY + 6; // Return new Y position
    };

    // Function to generate PDF content for a single order detail and append to an existing jsPDF document
    const generateAndAppendOrderDetailPdfContent = (docInstance, orderDetailsData) => {
        const orderDetails = orderDetailsData.getOrderDetailsById;

        if (!orderDetails) {
            console.warn("No order details provided to generate PDF content for an order.");
            return;
        }

        const filteredItems = (orderDetails.items) || [];
        const billDetails = (orderDetails.billDetails) || {};
        const customerType = orderDetails.customerType; 

        docInstance.setFontSize(12);
        let currentY = 20;

        // Header for the Order Details
        docInstance.setFontSize(16);
        docInstance.text('ORDER DETAILS', docInstance.internal.pageSize.getWidth() / 2, 10, { align: 'center' });
        docInstance.setFontSize(12);

        // Order Details
        docInstance.text(`Order Number: ${orderDetails._id || 'N/A'}`, 14, currentY);
        currentY += 6;
        docInstance.text(`Order Date: ${formatDate(orderDetails.orderDate || null)}`, 14, currentY); 
        currentY += 6;
        docInstance.text(`Customer Name: ${(orderDetails.customer && orderDetails.customer.name) ? orderDetails.customer.name : 'N/A'}`, 14, currentY);
        currentY += 6;
        docInstance.text(`Customer Phone: ${(orderDetails.customer && orderDetails.customer.phone) ? orderDetails.customer.phone : 'N/A'}`, 14, currentY);
        currentY += 6;

        const itemCount = filteredItems.length;
        docInstance.text(`Item Count: ${itemCount}`, 14, currentY);
        currentY += 6;

        const address = (orderDetails.deliveryAddress) ?
            `${(orderDetails.deliveryAddress.details || '')} ,${(orderDetails.deliveryAddress.deliveryAddress || '')}`
            :
            'N/A';
        const wrappedAddress = docInstance.splitTextToSize(address, 180);
        docInstance.text('Customer Address:', 14, currentY);
        currentY += 6;
        docInstance.text(wrappedAddress, 14, currentY);
        currentY += wrappedAddress.length * 6;

        docInstance.text(`Customer Type: ${getCustomerTypeLabel(customerType)}`, 14, currentY);
        currentY += 6;

        // Payment Details - Will now use the new mappings
        docInstance.text(`Payment Method: ${getPaymentMethod(orderDetails.paymentMethod)}`, 14, currentY);
        currentY += 6;
        docInstance.text(`Payment Status: ${getPaymentStatus(orderDetails.paymentStatus)}`, 14, currentY);
        currentY += 10;
        docInstance.text(
        `Locked Rider Email: ${
            orderDetails.orderState && orderDetails.orderState.lockedRiderEmail
            ? orderDetails.orderState.lockedRiderEmail
            : 'N/A'
        }`,
        14,
        currentY
        );
        currentY += 6;

        const tableColumn = ['Sl No', 'Name', 'Quantity', 'MRP', 'Selling Price', 'Net Price'];
        const tableRows = filteredItems.map((row, index) => {
            const sellingPrice = getSellingPrice(row.prices, customerType);
            const netPrice = row.quantity * sellingPrice;
            return [
                index + 1,
                row.name,
                row.quantity,
                row.mrp,
                sellingPrice,
                netPrice
            ];
        });

        docInstance.autoTable({
            head: [tableColumn],
            body: tableRows,
            startY: currentY,
            theme: 'grid',
        });

        currentY = docInstance.lastAutoTable.finalY + 10;

        const rightAlignX = docInstance.internal.pageSize.getWidth() - 30;
        const labelX = rightAlignX - 70;

        // Billing Details
        if (billDetails) {
            currentY = renderBillingDetail(docInstance, 'MRP:', billDetails.itemsMrp || 0, currentY, rightAlignX, labelX);
            currentY = renderBillingDetail(docInstance, 'Items Discount:', billDetails.itemSavings || 0, currentY, rightAlignX, labelX);
            currentY = renderBillingDetail(docInstance, 'Items Total Price:', billDetails.itemsTotal || 0, currentY, rightAlignX, labelX);

            if (billDetails.couponInfo && billDetails.couponInfo.applied) {
                currentY = renderBillingDetail(docInstance, 'Coupon:', billDetails.couponInfo.amount || 0, currentY, rightAlignX, labelX);
            }
            if (billDetails.walletInfo && billDetails.walletInfo.applied) {
                currentY = renderBillingDetail(docInstance, 'Wallet:', billDetails.walletInfo.amount || 0, currentY, rightAlignX, labelX);
            }
            if (billDetails.referralInfo && billDetails.referralInfo.applied) {
                currentY = renderBillingDetail(docInstance, 'ReferralInfo:', billDetails.referralInfo.amount || 0, currentY, rightAlignX, labelX);
            }
            if (billDetails.nestInfo && billDetails.nestInfo.applied) {
                currentY = renderBillingDetail(docInstance, 'Nest:', billDetails.nestInfo.amount || 0, currentY, rightAlignX, labelX);
            }

            if (billDetails.chargesWaived === 3) {
                currentY = renderBillingDetail(docInstance, 'Delivery Charges:', billDetails.deliveryCharge || 0, currentY, rightAlignX, labelX, true);
                currentY = renderBillingDetail(docInstance, 'Handling Charges:', billDetails.handlingCharge || 0, currentY, rightAlignX, labelX, true);
            } else if (billDetails.chargesWaived === 2) {
                currentY = renderBillingDetail(docInstance, 'Delivery Charges:', billDetails.deliveryCharge || 0, currentY, rightAlignX, labelX, true);
                currentY = renderBillingDetail(docInstance, 'Handling Charges:', billDetails.handlingCharge || 0, currentY, rightAlignX, labelX);
            } else if (billDetails.chargesWaived === 1) {
                currentY = renderBillingDetail(docInstance, 'Delivery Charges:', billDetails.deliveryCharge || 0, currentY, rightAlignX, labelX);
                currentY = renderBillingDetail(docInstance, 'Handling Charges:', billDetails.handlingCharge || 0, currentY, rightAlignX, labelX, true);
            } else {
                currentY = renderBillingDetail(docInstance, 'Delivery Charges:', billDetails.deliveryCharge || 0, currentY, rightAlignX, labelX);
                currentY = renderBillingDetail(docInstance, 'Handling Charges:', billDetails.handlingCharge || 0, currentY, rightAlignX, labelX);
            }

            const pageWidth = docInstance.internal.pageSize.getWidth();
            const halfPageWidth = pageWidth / 2;
            const rightSideWidth = pageWidth - 14;
            currentY += 2;
            docInstance.line(halfPageWidth, currentY, rightSideWidth, currentY);
            currentY += 4;

            currentY = renderBillingDetail(docInstance, 'Total Savings:', billDetails.totalSaving || 0, currentY, rightAlignX, labelX);
            currentY = renderBillingDetail(docInstance, 'Net Price:', billDetails.grandTotal || 0, currentY, rightAlignX, labelX);
        }
    };


    // Main function to print all order details
    const printAllOrderDetails = async (orderIds) => {
        if (!orderIds || orderIds.length === 0) {
            alert("No orders selected for printing.");
            return;
        }

        const combinedDoc = new jsPDF();

        try {
            for (let i = 0; i < orderIds.length; i++) {
                const orderId = orderIds[i];
                console.log("Attempting to fetch order details for ID:", orderId);

                const { data, errors } = await client.query({
                    query: GET_ORDER_DETAILS_BY_ID,
                    variables: { orderId },
                    fetchPolicy: "network-only",
                });

                if (errors) {
                    console.error(`GraphQL errors for order details ${orderId}:`, errors);
                    combinedDoc.text(`Error fetching details for Order ID: ${orderId}`, 14, combinedDoc.internal.pageSize.height / 2);
                    if (i < orderIds.length - 1) {
                        combinedDoc.addPage();
                    }
                    continue;
                }

                if (data && data.getOrderDetailsById) {
                    console.log(`Order details received for order ${orderId}:`, data.getOrderDetailsById);

                    if (i > 0) {
                        combinedDoc.addPage();
                    }
                    generateAndAppendOrderDetailPdfContent(combinedDoc, data);

                } else {
                    console.warn(`No order details found for order ${orderId} in 'getOrderDetailsById' field.`);
                    combinedDoc.text(`No details found for Order ID: ${orderId}`, 14, combinedDoc.internal.pageSize.height / 2);
                    if (i < orderIds.length - 1) {
                        combinedDoc.addPage();
                    }
                }
            }

            if (combinedDoc.internal.pages.length > 0) {
                combinedDoc.autoPrint();

                const pdfBlob = combinedDoc.output('blob');
                const pdfUrl = URL.createObjectURL(pdfBlob);

                const printWindow = window.open(pdfUrl);
                if (printWindow) {
                    printWindow.onload = function () {
                        printWindow.focus();
                        printWindow.print();
                    };
                } else {
                    alert("Popup blocked. Please allow popups for this site to print.");
                }
            } else {
                alert("No order details could be generated for printing.");
            }

        } catch (error) {
            console.error("Error printing all order details:", error);
            alert("An error occurred while generating PDFs. Check console for details.");
        }
    };

    useEffect(() => {
        client
            .query({ query: GET_LOCAL_STORE_CUSTOMER_DELIVERY_SLOT })
            .then(function (response) {
                const fetchedSlots =
                    (response && response.data && response.data.getLocalStoreCustomerDeliverySlot && response.data.getLocalStoreCustomerDeliverySlot.timeSlot)
                        ? response.data.getLocalStoreCustomerDeliverySlot.timeSlot
                        : [];
                setTimeSlots(fetchedSlots);
            })
            .catch(function (error) {
                console.error('Error fetching time slots:', error);
            });
    }, [client]);

    const [fetchOrders, { loading: dataLoading }] = useLazyQuery(
        GET_ORDERS_BY_TIME_SLOT,
        {
            onCompleted: function (data) {
                const orders =
                    (data && data.getOrdersByTimeSlot) ? data.getOrdersByTimeSlot : [];
                setTableData(orders);
                setTotalCount(orders.length);
            },
            onError: function (error) {
                console.error('Error fetching orders:', error);
            },
            fetchPolicy: "network-only",
        }
    );

    const handleFetchOrders = () => {
        if (!selectedTimeSlot) {
            alert('Please select a time slot.');
            return;
        }

        fetchOrders({
            variables: {
                timeSlotId: selectedTimeSlot,
                orderDate,
                orderStatus: parseInt(orderStatus, 10),
                skip: pagination.skip,
                limit: pagination.limit
            },
        });
    };

    const downloadCSV = async () => {
        if (!tableData.length) {
            alert('No data to download.');
            return;
        }

        const headers = [
            'Order ID',
            'Customer Name',
            'Phone',
            'Order Amount',
            'Payment Method',
            'Order Status',
            'Order Date'
        ];
        const csvRows = [headers.join(',')];

        tableData.forEach((order) => {
            const rowData = [
                order._id,
                (order.customer && order.customer.name) ? order.customer.name : 'N/A',
                (order.customer && order.customer.phone) ? order.customer.phone : 'N/A',
                order.orderAmount,
                getPaymentMethod(order.paymentMethod),
                (order.orderState && statuses[order.orderState.status]) ? statuses[order.orderState.status] : 'N/A',
                formatDate(order.orderDate),
            ];
            csvRows.push(rowData.map(item => `"${String(item).replace(/"/g, '""')}"`).join(','));
        });

        const csvContent = csvRows.join('\n');
        const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
        const link = document.createElement('a');
        const url = URL.createObjectURL(blob);
        link.setAttribute('href', url);
        link.setAttribute('download', 'OrdersbyTimeSlot.csv');
        link.style.visibility = 'hidden';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
        URL.revokeObjectURL(url);
    };

    const columns = [
        { name: 'Order ID', selector: (row) => row._id, sortable: true,wrap: true },
        { name: 'Customer Name', selector: (row) => (row.customer && row.customer.name) ? row.customer.name : 'N/A', sortable: true },
        { name: 'Phone', selector: (row) => (row.customer && row.customer.phone) ? row.customer.phone : 'N/A', sortable: true },
        { name: 'Order Amount', selector: (row) => row.orderAmount, sortable: true },
        { name: 'Payment Method', selector: (row) => getPaymentMethod(row.paymentMethod), sortable: true },
        { name: 'Order Status', selector: (row) => (row.orderState && statuses[row.orderState.status] ? statuses[row.orderState.status] : 'N/A'), sortable: true },
        { name: 'Order Date', selector: (row) => formatDate(row.orderDate), sortable: true },
    ];

    return (
        <Container className={globalClasses.flex}>
            <Box sx={{ mb: 3, mt: 3 }}>
                <Grid container spacing={2} alignItems="center">
                    <Grid item xs={3}>
                        <TextField
                            select
                            label="Time Slot"
                            value={selectedTimeSlot}
                            onChange={(e) => setSelectedTimeSlot(e.target.value)}
                            fullWidth
                            sx={{
                                input: { color: 'black' },
                                '.MuiSelect-select': { color: 'black' }
                            }}
                        >
                            {timeSlots.map((slot) => (
                                <MenuItem key={slot._id} value={slot._id} sx={{ color: 'black' }}>
                                    {slot.start} - {slot.end}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            label="Select Date"
                            type="date"
                            value={orderDate}
                            onChange={(e) => setOrderDate(e.target.value)}
                            InputLabelProps={{ shrink: true }}
                            fullWidth
                            sx={{ input: { color: 'black' } }}
                        />
                    </Grid>
                    <Grid item xs={3}>
                        <TextField
                            select
                            label="Order Status"
                            value={orderStatus}
                            onChange={(e) => setOrderStatus(e.target.value)}
                            fullWidth
                            sx={{
                                input: { color: 'black' },
                                '.MuiSelect-select': { color: 'black' }
                            }}
                        >
                            {Object.entries(statuses).map(([key, value]) => (
                                <MenuItem key={key} value={key} sx={{ color: 'black' }}>
                                    {value}
                                </MenuItem>
                            ))}
                        </TextField>
                    </Grid>
                </Grid>
                <Box mt={3}>
                    <Button onClick={handleFetchOrders} variant="contained" color="primary" sx={{ mr: 2 }}>
                        <Icon icon="ic:baseline-search" style={{ marginRight: '8px' }} /> Fetch Orders
                    </Button>
                    <Button onClick={downloadCSV} variant="contained" color="primary">
                        <Icon icon="mdi:download" style={{ marginRight: '8px' }} /> Download CSV
                    </Button>
                    <Button
                        onClick={() => {
                            const orderIds = tableData.map((order) => order._id);
                            if (orderIds.length === 0) {
                                alert("No orders to print.");
                                return;
                            }
                            printAllOrderDetails(orderIds);
                        }}
                        variant="contained"
                        color="primary"
                        sx={{ marginLeft: 2 }}
                    >
                        <Icon icon="mdi:printer" style={{ marginRight: "8px" }} /> Print All Order Details
                    </Button>

                </Box>
            </Box>

            {dataLoading ? (
                <CustomLoader />
            ) : (
                <DataTable
                    columns={columns}
                    data={tableData}
                    noHeader
                    pagination={false}
                    customStyles={customStyles}
                    highlightOnHover
                />
            )}
            <Box mt={2}>
                <Typography variant="subtitle1">Total Orders: {totalCount}</Typography>
            </Box>
        </Container>
    );
};

export default OrdersbyTimeSlot;

